import type { ReviewStats } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

/**
 * The stays that produced no review at all.
 *
 * The rest of this page is about the reviews that arrived — their stars,
 * their sub-ratings, the handful that came in below five. But far more stars
 * are lost to silence than to four-star reviews, and until now that only
 * appeared as a single review-rate percentage in the KPI strip.
 *
 * `no_review` and `pending_no_review` are disjoint in admin-api's stats
 * service: a stay still inside its review window is counted as pending and
 * skipped before `stays` is incremented. So the silent count is already
 * final — those stays can never be reviewed now — and the pending count is
 * the only part still worth chasing.
 */
export function SilentStays({ stats }: { stats: ReviewStats }) {
  const p = stats.portfolio
  if (p.stays === 0) return null

  // Worst offenders first, and only properties that actually lost something:
  // a house with a perfect record is not a row worth a bar.
  const rows = stats.properties
    .filter(x => x.no_review > 0 && x.stays > 0)
    .sort((a, b) => b.no_review / b.stays - a.no_review / a.stays)
    .slice(0, 8)
  const worstShare = rows.length > 0 ? rows[0].no_review / rows[0].stays : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>The reviews you never got</CardTitle>
        <CardDescription>
          A silent stay costs more ranking volume than a four-star review, and there are far more of
          them.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-card bg-surface2 px-3 py-2.5">
            <p className="text-xs text-textMuted">Silent stays</p>
            <p className="text-xl font-semibold tabular-nums text-text">{p.no_review}</p>
            <p className="text-[11px] text-textMuted">of {p.stays}, window closed</p>
          </div>
          <div className="rounded-card bg-surface2 px-3 py-2.5">
            <p className="text-xs text-textMuted">Still open</p>
            <p className="text-xl font-semibold tabular-nums text-text">{p.pending_no_review}</p>
            <p className="text-[11px] text-textMuted">chaseable now</p>
          </div>
          <div className="rounded-card bg-surface2 px-3 py-2.5">
            <p className="text-xs text-textMuted">Sub-five</p>
            <p className="text-xl font-semibold tabular-nums text-text">{p.sub_five}</p>
            <p className="text-[11px] text-textMuted">of {p.reviews} reviews</p>
          </div>
        </div>

        {rows.length > 0 && (
          <>
            <p className="mb-2 mt-5 text-xs font-medium text-textMuted">
              Where the silence is worst
            </p>
            <div className="space-y-2">
              {rows.map(x => {
                const share = x.no_review / x.stays
                // Scaled to the worst property rather than to 100%: every
                // share here sits well under half, so a 0-100 scale renders
                // eight short stubs that can't be told apart.
                const pct = worstShare > 0 ? Math.max(3, (share / worstShare) * 100) : 0
                return (
                  <Tooltip key={x.property_id}>
                    <TooltipTrigger asChild>
                      <div
                        tabIndex={0}
                        className="grid cursor-default grid-cols-[minmax(0,11rem)_1fr_58px] items-center gap-3 focus-visible:outline-hidden"
                      >
                        <span className="truncate text-xs text-textMuted">{x.name}</span>
                        <div className="h-2 overflow-hidden rounded-sm bg-surface2">
                          <div
                            className="h-2 rounded-sm bg-borderStrong"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-right text-xs tabular-nums text-textMuted">
                          {x.no_review}/{x.stays}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {x.name}: {x.no_review} of {x.stays} closed stays left no review
                      {x.review_rate != null ? ` (${x.review_rate}% reviewed)` : ''}
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </div>
          </>
        )}

        {p.review_rate != null && (
          <p className="mt-4 border-t border-border pt-3 text-xs text-textMuted">
            <span className="text-text">{p.review_rate}%</span> of closed stays left a review. The{' '}
            {p.pending_no_review} still open are the only ones you can still do anything about.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
